import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  :root {
    --primary-color: #008ecc;
    --primary-hover: #0077ab;
    --accent-color: #008ecc;
    --text-color: #1c2d3d;
    --text-dark: #0c1b2a;
    --text-muted: #5b6b7c;
    --primary-light: rgba(0, 142, 204, 0.1);
    --surface: #f4f8fb;
    --surface-alt: #eef6fb;
    --border: #dce8f0;
    --background-color: #ffffff;
    --footer-bg-color: #f5f5f5;

    /* Depth, motion and shape */
    --ring: rgba(0, 142, 204, 0.35);
    --radius-sm: 10px;
    --radius-md: 16px;
    --radius-lg: 22px;
    --shadow-xs: 0 1px 2px rgba(12, 27, 42, 0.06);
    --shadow-sm: 0 4px 14px rgba(12, 27, 42, 0.06);
    --shadow-md: 0 14px 32px rgba(12, 27, 42, 0.1);
    --shadow-lg: 0 26px 60px rgba(12, 27, 42, 0.16);
    --gradient-brand: linear-gradient(135deg, #0674ae 0%, #00a6e8 58%, #4cc9f0 100%);
    --gradient-ink: linear-gradient(165deg, #0a1c2d 0%, #0f2740 58%, #0b334d 100%);
    --header-height: 5rem;
    --ease: cubic-bezier(0.22, 1, 0.36, 1);
  }

  html {
    /* 18px base: comfortably readable across the site */
    font-size: 112.5%;
    scroll-behavior: smooth;
  }

  body {
    background-color: var(--background-color);
    color: var(--text-color);
    font-family: 'Barlow', sans-serif;
    font-size: 1rem;
    line-height: 1.65;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  /* Khmer web font (self-hosted, LGPL, by Danh Hong).
     unicode-range keeps Barlow for Latin text; Khmer OS Siemreap renders the
     Khmer glyphs. Only the Regular weight exists — bold is browser-synthesized,
     which is how this font is normally used. */
  @font-face {
    font-family: 'Khmer OS Siemreap';
    src: url('../../public/fonts/KhmerOS_siemreap.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
    unicode-range: U+1780-17FF, U+19E0-19FF, U+200C-200D, U+25CC;
  }

  /* Khmer typography: Barlow has no Khmer glyphs, so switch the font chain.
     Khmer stacks consonant + vowel marks deeper than Latin, so it needs more
     line height, and tight/negative letter-spacing breaks glyph cohesion. */
  html[lang='km'] body {
    font-family: 'Khmer OS Siemreap', 'Noto Sans Khmer', 'Barlow', sans-serif;
    line-height: 1.85;
  }

  html[lang='km'] button,
  html[lang='km'] .button,
  html[lang='km'] input,
  html[lang='km'] textarea,
  html[lang='km'] select {
    font-family: 'Khmer OS Siemreap', 'Noto Sans Khmer', 'Barlow', sans-serif;
  }

  html[lang='km'] h1,
  html[lang='km'] h2,
  html[lang='km'] h3,
  html[lang='km'] h4,
  html[lang='km'] h5,
  html[lang='km'] h6,
  html[lang='km'] p,
  html[lang='km'] span,
  html[lang='km'] a,
  html[lang='km'] li,
  html[lang='km'] blockquote,
  html[lang='km'] label,
  html[lang='km'] button {
    letter-spacing: normal;
  }

  html[lang='km'] h1,
  html[lang='km'] h2,
  html[lang='km'] h3,
  html[lang='km'] h4,
  html[lang='km'] h5,
  html[lang='km'] h6 {
    line-height: 1.6;
    text-transform: none;
  }

  ::selection {
    background: rgba(0, 142, 204, 0.18);
    color: var(--text-dark);
  }

  :focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 3px;
    border-radius: 4px;
  }

  @media (prefers-reduced-motion: reduce) {
    html {
      scroll-behavior: auto;
    }
    *,
    *::before,
    *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
    }
  }

  .btn-primary {
    background-color: var(--primary-color);
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }

  .btn-primary:hover {
    background-color: #006e9f;
  }

  a.primary-link {
    color: var(--primary-color);
    text-decoration: none;
    transition: color 0.3s ease;
  }

  a.primary-link:hover {
    color: #006e9f;
  }

  .highlight-text {
    color: var(--primary-color);
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  /* Common styling for buttons */
  button, .button {
    cursor: pointer;
    font-family: 'Barlow', sans-serif;
  }

  /* Primary button styling */
  .btn-primary, .button.primary {
    background-color: var(--primary-color);
    color: white;
    border: none;
    border-radius: 4px;
    padding: 10px 20px;
    font-weight: 500;
    transition: all 0.3s ease;
  }

  .btn-primary:hover, .button.primary:hover {
    background-color: #006e9f;
  }

  /* Container class for consistent spacing */
  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1.25rem;
  }

  @media (min-width: 1024px) {
    .container {
      padding: 0 2rem;
    }
  }

  /* Section styling */
  section {
    padding: 0.4rem 0;
  }

  /* Heading styles */
  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    font-weight: 600;
    line-height: 1.25;
  }

  p {
    text-wrap: pretty;
  }

  /* Hero section styling */
  .hero-section {
    position: relative;
    min-height: 70vh;
    display: flex;
    align-items: center;
  }
`;

export default GlobalStyles;
